import { Fragment, useCallback, useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { LabelChip } from '@components/Label';
import { CssNoOptions } from '@components/Select';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import {
    StyledAutocomplete,
    CustomPopper,
    ErrorWrapper,
    AllOption,
    OneGroupOption,
    GroupOption,
    GroupWrapper,
    GroupHeader,
    BaseOptionWrapper,
    CssCheckbox,
    ErrText,
} from './styles';

const BaseOptionComponent = ({ id, label, ...props }) => {
    return (
        <BaseOptionWrapper key={id} component="li" {...props}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label}
            </Typography>
        </BaseOptionWrapper>
    );
};

const Autocomplete = ({
    className,
    label,
    options,
    onChangeAutoComplete,
    ValueComponent,
    OptionComponent,
    optionLabelProp = 'label',
    inputProps = {},
    readOnly,
    inputRef,
    error,
    groupMultiProps = null,
    disabled,
    ...props
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [isFocused, setIsFocused] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
        if (props?.value?.length > 0) {
            setSelectedItems(props?.value || []);
        }
    }, [props?.value]);

    const extendedOptions = useMemo(
        () => [...(groupMultiProps ? [{ id: 'all', label: t('common.all') }] : []), ...options],
        [options, groupMultiProps],
    );

    const onSelectItems = useCallback(
        items => {
            setSelectedItems(items);
            onChangeAutoComplete(items);
        },
        [onChangeAutoComplete],
    );

    const checkOption = useCallback(option => selectedItems.some(c => c.id === option.id), [selectedItems]);

    const checkAllOption = useCallback(() => {
        return {
            all: options.length === selectedItems.length,
            some: selectedItems.length > 0 && options.length !== selectedItems.length,
        };
    }, [options, selectedItems]);

    const selectAll = () => {
        const selectAllItems = options.length === selectedItems.length ? [] : options;
        onSelectItems(selectAllItems);
    };

    const checkGroup = useCallback(
        group => {
            const groupLength = options.filter(c => c.group === group).length;
            const selectedGroupLength = selectedItems.filter(c => c.group === group).length;
            return {
                all: groupLength === selectedGroupLength,
                some: selectedGroupLength > 0 && groupLength !== selectedGroupLength,
            };
        },
        [options, selectedItems],
    );

    const selectGroup = group => {
        const groupedOptions = options.filter(c => c.group === group);
        const selectedGroupedOptions = selectedItems.filter(c => c.group === group);
        let newSelectedItems = [];
        if (selectedGroupedOptions.length > 0) {
            newSelectedItems = [...selectedItems.filter(c => c.group !== group)];
        } else {
            newSelectedItems = [...selectedItems, ...groupedOptions];
        }
        onSelectItems(newSelectedItems);
    };

    const hasOneGroup = useMemo(
        () => groupMultiProps && new Set(options.map(option => option.group)).size === 1,
        [options],
    );

    const customPopper = useCallback(popperProps => <CustomPopper {...popperProps} />, []);

    // INFO: partlyAssignedCallback throws error in console regarding to html attribute
    const { partlyAssignedCallback, ...restGroupMultiProps } = groupMultiProps || {}; // eslint-disable-line

    return (
        <ErrorWrapper error={!!error} className={className}>
            <StyledAutocomplete
                {...props}
                multiple
                options={extendedOptions}
                readOnly={readOnly || disabled}
                value={props?.value || selectedItems}
                getOptionLabel={option => option.id}
                disabled={disabled}
                renderInput={params => (
                    <TextField
                        {...params}
                        ref={inputRef}
                        variant="outlined"
                        label={label}
                        required={props.required}
                        inputProps={{
                            ...params.inputProps,
                            ...(groupMultiProps
                                ? {}
                                : {
                                      onFocus: () => setIsFocused(true),
                                      onBlur: () => setIsFocused(false),
                                      ...inputProps,
                                  }),
                        }}
                        error={!!error}
                    />
                )}
                onChange={(_, items, reason) => {
                    onSelectItems(items);
                    if (!groupMultiProps) setIsFocused(reason === 'clear');
                }}
                {...(groupMultiProps
                    ? {
                          inputValue: '',
                          disableCloseOnSelect: true,
                          groupBased: !!groupMultiProps,
                          groupBasedSelected: selectedItems.length > 0,
                          groupBy: option => option.group,
                          renderOption: (props, option) => {
                              const { all, some } = checkAllOption();
                              return option.id === 'all' ? (
                                  <AllOption {...props} component="li" key={option.id} onClick={selectAll}>
                                      <CssCheckbox checked={all} indeterminate={some} />
                                      <Typography variant="body2" sx={{ lineHeight: '32px !important' }}>
                                          {t('common.all')}
                                      </Typography>
                                  </AllOption>
                              ) : hasOneGroup ? (
                                  <OneGroupOption
                                      {...props}
                                      key={option.id}
                                      component="li"
                                      aria-selected={checkOption(option)}
                                  >
                                      <CssCheckbox checked={checkOption(option)} />
                                      <Typography variant="body2">{option.label}</Typography>
                                  </OneGroupOption>
                              ) : (
                                  <GroupOption
                                      {...props}
                                      key={option.id}
                                      component="li"
                                      aria-selected={checkOption(option)}
                                  >
                                      <CssCheckbox checked={checkOption(option)} />
                                      <Typography variant="body2">{option.label}</Typography>
                                  </GroupOption>
                              );
                          },
                          renderGroup: params => {
                              if (!params.group || hasOneGroup) {
                                  return (
                                      <GroupWrapper
                                          key={params.key}
                                          sx={{
                                              borderBottom: !params.group ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                                              paddingBottom: !params.group ? '8px' : 0,
                                              paddingTop: !params.group ? 0 : '8px',
                                          }}
                                      >
                                          {params.children}
                                      </GroupWrapper>
                                  );
                              }
                              const { all, some } = checkGroup(params.group);
                              return (
                                  <Fragment key={params.key}>
                                      <div
                                          style={{ display: 'flex', alignItems: 'center', gap: '3px', margin: '3px 0' }}
                                      >
                                          <i />
                                          <CssCheckbox
                                              checked={all}
                                              indeterminate={some}
                                              onChange={() => selectGroup(params.group)}
                                          />
                                          <GroupHeader variant="caption">{params.group}</GroupHeader>
                                      </div>
                                      <GroupWrapper>{params.children}</GroupWrapper>
                                  </Fragment>
                              );
                          },
                          renderTags: () => {
                              return selectedItems.map((option, index) => (
                                  <LabelChip
                                      key={index}
                                      type={option.label}
                                      isPartlyAssigned={groupMultiProps?.partlyAssignedCallback?.(option.id)}
                                      label={
                                          <Typography variant="caption" sx={{ display: 'block', height: 14 }}>
                                              {option.label}
                                          </Typography>
                                      }
                                      clickable
                                      deleteIcon={<CloseIcon onMouseDown={e => e.stopPropagation()} />}
                                      onDelete={() => {
                                          const filteredItems = selectedItems.filter(item => item.id !== option.id);
                                          onSelectItems(filteredItems);
                                      }}
                                      sx={{ margin: '0 3px 3px 0' }}
                                  />
                              ));
                          },
                          ...restGroupMultiProps,
                      }
                    : {
                          open: isFocused,
                          renderOption: (props, option) =>
                              OptionComponent ? (
                                  <OptionComponent {...props} key={option.id} option={option} />
                              ) : (
                                  <BaseOptionComponent
                                      {...props}
                                      key={option.id}
                                      id={option.id}
                                      label={option[optionLabelProp]}
                                  />
                              ),
                          renderTags: options => {
                              return options.map(option =>
                                  ValueComponent ? (
                                      <ValueComponent {...props} key={option.id} option={option} />
                                  ) : (
                                      <BaseOptionComponent
                                          key={option.id}
                                          id={option.id}
                                          label={option[optionLabelProp]}
                                      />
                                  ),
                              );
                          },
                      })}
                noOptionsText={<CssNoOptions>{t('common.noOptions')}</CssNoOptions>}
                PopperComponent={customPopper}
                ListboxProps={{
                    role: 'list-box',
                    sx: {
                        overflow: 'auto !important',
                        '& .MuiAutocomplete-option': {
                            minHeight: '32px',
                            flexWrap: 'nowrap',
                            '@media (max-width: 600px)': {
                                width: 'calc(100% - 16px)',
                            },
                        },
                    },
                }}
                popupIcon={null}
                error={!!error}
            />
            {error && error?.message && <ErrText>{t(error.message)}</ErrText>}
        </ErrorWrapper>
    );
};

export default Autocomplete;
